"use client";

import { useCallback, useEffect } from "react";
import { ReadonlyURLSearchParams } from "next/navigation";
import { useInstantSearch } from "react-instantsearch";
import { FILTER_CATEGORIES } from "@/components/Search/constants";
import { getRefinementListFromSearchParams } from "@/components/Search/utils/getRefinementListFromSearchParams";
import { useSearchIndex } from "@/components/Search/components/SearchContextProvider";

type Props = {
  searchParams: ReadonlyURLSearchParams | URLSearchParams;
};

/**
 * Keeps InstantSearch facet refinements aligned with the URL when params change outside the
 * filter widgets (e.g. a facet removed from the query string). `initialUiState` only applies
 * on first mount, so this effect pushes URL-derived refinements into the index UI state.
 *
 * Also listens for `popstate` events so that refinements are cleared when filters are removed
 * via `history.replaceState` from outside Next.js routing (e.g. DynamicFlow filter pills).
 */
export function SyncInstantSearchRefinementsFromUrl({ searchParams }: Props) {
  const { searchIndex } = useSearchIndex();
  const { setIndexUiState } = useInstantSearch();
  const signature = searchParams.toString();

  const syncRefinements = useCallback(
    (params: URLSearchParams) => {
      const fromUrl = getRefinementListFromSearchParams(params, searchIndex);

      setIndexUiState((prev) => {
        const nextList = { ...(prev.refinementList ?? {}) };

        FILTER_CATEGORIES.forEach((facet) => {
          if (fromUrl[facet]?.length) {
            nextList[facet] = fromUrl[facet];
          } else {
            delete nextList[facet];
          }
        });

        return {
          ...prev,
          refinementList: nextList,
        };
      });
    },
    [searchIndex, setIndexUiState],
  );

  useEffect(() => {
    const parsed =
      signature.length > 0
        ? new URLSearchParams(signature)
        : new URLSearchParams();
    syncRefinements(parsed);
  }, [signature, syncRefinements]);

  useEffect(() => {
    const handlePopState = () => {
      syncRefinements(new URLSearchParams(window.location.search));
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [syncRefinements]);

  return null;
}
